'use client';

import { useTranslations } from 'next-intl';
import { Eye, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { PermissionGate } from '@/features/auth';
import type { SortingSession, SortingSessionStatus } from '../types';

interface SortingCompactRowProps {
  session: SortingSession;
  onView: (session: SortingSession) => void;
  onDelete: (session: SortingSession) => void;
}

const STATUS_STYLES: Record<SortingSessionStatus, string> = {
  pending:
    'bg-amber-100 text-amber-800 hover:bg-amber-100 dark:bg-amber-900 dark:text-amber-200',
  in_progress:
    'bg-blue-100 text-blue-800 hover:bg-blue-100 dark:bg-blue-900 dark:text-blue-200',
  completed:
    'bg-green-100 text-green-800 hover:bg-green-100 dark:bg-green-900 dark:text-green-200'
};

export function SortingCompactRow({
  session,
  onView,
  onDelete
}: SortingCompactRowProps) {
  const t = useTranslations('sorting');

  const statusLabels: Record<SortingSessionStatus, string> = {
    pending: t('statusPending'),
    in_progress: t('statusInProgress'),
    completed: t('statusCompleted')
  };

  const sorted = session.sorted_containers ?? 0;
  const total = session.total_containers ?? 0;

  return (
    <div className='flex items-center gap-3 rounded-md border px-4 py-2.5'>
      {/* Left: box number */}
      <div className='min-w-0 flex-1'>
        <span className='truncate font-mono font-medium'>
          {session.box?.box_number ?? '-'}
        </span>
      </div>

      {/* Center: sorted/total containers count */}
      <span className='text-muted-foreground hidden text-sm md:inline'>
        {sorted}/{total} {t('sortedContainers').toLowerCase()}
      </span>

      {/* Right: status badge */}
      <Badge
        variant='secondary'
        className={cn('shrink-0 text-xs', STATUS_STYLES[session.status])}
      >
        {statusLabels[session.status] ?? session.status}
      </Badge>

      {/* Action buttons */}
      <div className='flex shrink-0 items-center gap-1'>
        <Button
          variant='ghost'
          size='icon'
          className='h-7 w-7'
          onClick={() => onView(session)}
        >
          <Eye className='h-3.5 w-3.5' />
        </Button>
        <PermissionGate permissions={['sorting.delete']}>
          <Button
            variant='ghost'
            size='icon'
            className='text-destructive h-7 w-7'
            onClick={() => onDelete(session)}
          >
            <Trash2 className='h-3.5 w-3.5' />
          </Button>
        </PermissionGate>
      </div>
    </div>
  );
}
